import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchTenants, requestSuperuserHandoff } from '../services/superuserApi';
import { useAuth } from '../context/AuthContext';
import { zigbeeConfig } from '../config/zigbee';
import { fetchModules, ModuleKey, ModuleState } from '../services/modulesApi';
import { useMyPush } from '../hooks/useMyPush';

const BOTTOM_NAV_ITEMS = [
    { path: '/',             icon: '📊', label: 'Översikt',     minLevel: 1 },
    { path: '/devices',      icon: '📱', label: 'Enheter',      minLevel: 1 },
    { path: '/inspections',  icon: '✅', label: 'Egenkontroll', minLevel: 1, module: 'inspections' as ModuleKey },
    { path: '/surveillance', icon: '🔔', label: 'Bevakning',    minLevel: 1 },
];

/** module: menyvalet visas bara om modulen är påslagen för kunden (huvudadmin styr). */
interface NavItem { path: string; label: string; minLevel: number; module?: ModuleKey; }
interface NavGroup { title: string | null; items: NavItem[]; }

// Menyn i grupper: samma sidor som förut, bara sorterade efter vad de hör ihop med.
const NAV_GROUPS: NavGroup[] = [
    { title: null, items: [
        { path: '/',                   label: '📊 Översikt',     minLevel: 1 },
    ] },
    { title: 'Enheter', items: [
        { path: '/devices',            label: '📱 Enheter',      minLevel: 1 },
        { path: '/device-schedules',   label: '⏱️ Schemalagd styrning', minLevel: 1, module: 'device_schedules' },
        { path: '/zones',              label: '🗂️ Zoner',        minLevel: 2 },
        { path: '/network-topology',   label: '🕸️ Nätverk',      minLevel: 2 },
        { path: '/gateways',           label: '📡 Gateways',     minLevel: 3 },
    ] },
    { title: 'Bevakning', items: [
        { path: '/surveillance',       label: '🔔 Bevakning',    minLevel: 1 },
        { path: '/data-visualization', label: '📈 Mätvärden',    minLevel: 1 },
        { path: '/setpoints',          label: '🎯 Börvärden',    minLevel: 2 },
    ] },
    { title: 'Egenkontroll', items: [
        { path: '/inspections',        label: '✅ Egenkontroll', minLevel: 1, module: 'inspections' },
        { path: '/qr-points',          label: '🔗 QR-punkter',   minLevel: 2, module: 'inspections' },
    ] },
    { title: 'Inställningar', items: [
        { path: '/contacts',           label: '👤 Kontakter',    minLevel: 2 },
        { path: '/users',              label: '👥 Användare',    minLevel: 2 },
        { path: '/heat-pump',          label: '🔥 Värmepump',    minLevel: 2, module: 'heatpump' },
        { path: '/shelly',             label: '⚡ Shelly',       minLevel: 2, module: 'shelly' },
        { path: '/kiosk-admin',        label: '🖥️ Kiosk',         minLevel: 2 },
        { path: '/billing',            label: '💰 Fakturaunderlag', minLevel: 3 },
    ] },
];

interface TenantInfo { id: string; display_name: string; active: number; }

// "Inte nu" på notisrutan gäller i 30 dagar på den här telefonen.
const PUSH_PROMPT_KEY = 'push-prompt-dismissed-at';
const PUSH_PROMPT_SNOOZE_MS = 30 * 24 * 3600 * 1000;

const userLevelLabel = (level?: string): string =>
    level === 'superuser' ? 'huvudadmin' : level === 'admin' ? 'admin' : 'användare';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, activeTenantId, token, selectTenant, isSuperuser } = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const [tenantName, setTenantName] = useState('');
    const [showTenantPicker, setShowTenantPicker] = useState(false);
    const [tenants, setTenants] = useState<TenantInfo[]>([]);
    const [tenantPickerLoading, setTenantPickerLoading] = useState(false);

    const userNumLevel = user?.userLevel === 'superuser' ? 3 : user?.userLevel === 'admin' ? 2 : 1;

    // Kundens moduler. Tills svaret kommit (eller om det misslyckas) visas alla menyval.
    const [modules, setModules] = useState<ModuleState | null>(null);
    useEffect(() => {
        if (!token) return;
        fetchModules().then(setModules).catch(() => setModules(null));
    }, [token, activeTenantId]);
    const visible = (item: { minLevel: number; module?: ModuleKey }) =>
        item.minLevel <= userNumLevel && (!item.module || !modules || modules[item.module]);

    useEffect(() => {
        const savedToken = localStorage.getItem('iot_token') ?? '';
        fetch(`${zigbeeConfig.apiBaseUrl}/tenant/info`, {
            headers: {
                'X-Tenant-Id': activeTenantId,
                ...(savedToken ? { Authorization: `Bearer ${savedToken}` } : {}),
            },
        })
            .then(r => r.ok ? r.json() : null)
            .then(info => { if (info?.display_name) setTenantName(info.display_name); })
            .catch(() => {});
    }, [activeTenantId]);

    // iOS PWA install hint: show only in Safari on iOS, not already installed, not dismissed
    const [showIosHint, setShowIosHint] = useState(() => {
        const ua = window.navigator.userAgent.toLowerCase();
        const isIos = /iphone|ipad|ipod/.test(ua);
        const isStandalone = (window.navigator as any).standalone === true;
        const dismissed = localStorage.getItem('ios-pwa-hint-dismissed') === '1';
        return isIos && !isStandalone && !dismissed;
    });

    const dismissIosHint = () => {
        localStorage.setItem('ios-pwa-hint-dismissed', '1');
        setShowIosHint(false);
    };

    // Notiser på den här telefonen: ruta tills man svarat, och ett val i mobilmenyn.
    const push = useMyPush(!!token);
    const [pushPromptDismissed, setPushPromptDismissed] = useState(() => {
        try { return Date.now() - Number(localStorage.getItem(PUSH_PROMPT_KEY) ?? 0) < PUSH_PROMPT_SNOOZE_MS; }
        catch { return false; }
    });
    const dismissPushPrompt = () => {
        try { localStorage.setItem(PUSH_PROMPT_KEY, String(Date.now())); } catch { /* ignorera */ }
        setPushPromptDismissed(true);
    };
    const showPushPrompt = push.available && !push.on && !pushPromptDismissed
        && typeof Notification !== 'undefined' && Notification.permission !== 'denied';

    const isActive = (path: string) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const pickerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!showTenantPicker) return;
        const handler = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setShowTenantPicker(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showTenantPicker]);

    const openTenantPicker = async () => {
        if (showTenantPicker) { setShowTenantPicker(false); return; }
        if (tenants.length === 0) {
            setTenantPickerLoading(true);
            try {
                setTenants(await fetchTenants<TenantInfo>());
            } catch { /* ignore */ }
            finally { setTenantPickerLoading(false); }
        }
        setShowTenantPicker(true);
    };

    const handleTenantSwitch = (tenantId: string) => {
        setShowTenantPicker(false);
        const parts = window.location.hostname.split('.');
        if (parts.length > 1 && token) {
            requestSuperuserHandoff(token)
                .then(({ code }) => {
                    const targetHost = [tenantId, ...parts.slice(1)].join('.');
                    window.location.href = `https://${targetHost}/?suCode=${encodeURIComponent(code)}`;
                })
                .catch(() => {});
        } else {
            selectTenant(tenantId);
            navigate('/');
        }
    };

    return (
        <div className="layout-root" style={{
            fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
            backgroundColor: '#f0f2f5',
            minHeight: '100vh',
        }}>
            {/* ── iOS PWA install hint ─────────────────────────────── */}
            {showIosHint && (
                <div className="ios-install-hint">
                    <span>📲 Installera DevIoT som app: tryck på <strong>Dela</strong> i Safari och välj <strong>Lägg till på hemskärmen</strong></span>
                    <button onClick={dismissIosHint}>Stäng</button>
                </div>
            )}

            {showPushPrompt && (
                <div className="push-prompt">
                    <span>🔔 Vill du få larm och påminnelser som notiser på den här enheten?</span>
                    <span className="push-prompt-actions">
                        <button className="primary" disabled={push.busy} onClick={push.enable}>
                            {push.busy ? '…' : 'Slå på'}
                        </button>
                        <button disabled={push.busy} onClick={dismissPushPrompt}>Inte nu</button>
                    </span>
                </div>
            )}

            {/* ── Top Navigation Bar ──────────────────────────────── */}
            <div className="nav-bar" style={{
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                padding: '14px 28px 12px',
                boxShadow: '0 2px 12px rgba(0,0,0,.2)',
                position: 'sticky',
                top: 0,
                zIndex: 100,
            }}>
                <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '12px',
                }}>
                    <h1 style={{
                        margin: 0, color: '#fff', fontSize: '18px',
                        fontWeight: 700, letterSpacing: '.3px', cursor: 'pointer',
                    }} onClick={() => navigate('/')}>
                        ⚡ DevIoT Platform{tenantName ? ` - ${tenantName}` : ''}
                    </h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="nav-user-info" style={{ fontSize: '13px', color: 'rgba(255,255,255,.75)' }}>
                            👤 <strong style={{ color: '#fff' }}>{user?.username}</strong>
                            <span style={{ opacity: 0.6, marginLeft: '4px' }}>({userLevelLabel(user?.userLevel)})</span>
                        </span>
                        {isSuperuser && (
                            <div ref={pickerRef} style={{ position: 'relative' }}>
                                <button
                                    onClick={openTenantPicker}
                                    style={{
                                        padding: '5px 12px', fontSize: '12px', fontWeight: 600,
                                        backgroundColor: 'rgba(255,255,255,.1)',
                                        border: '1px solid rgba(255,255,255,.25)',
                                        borderRadius: '6px', cursor: 'pointer', color: '#cde',
                                    }}
                                >
                                    🏢 {tenantName || activeTenantId} ▾
                                </button>
                                {showTenantPicker && (
                                    <div style={{
                                        position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                                        background: '#fff', borderRadius: '8px', minWidth: '200px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,.2)', zIndex: 200,
                                        overflow: 'hidden',
                                    }}>
                                        {tenantPickerLoading ? (
                                            <div style={{ padding: '12px 16px', color: '#888', fontSize: '13px' }}>Hämtar…</div>
                                        ) : tenants.filter(t => t.active).map(t => (
                                            <button
                                                key={t.id}
                                                onClick={() => handleTenantSwitch(t.id)}
                                                style={{
                                                    display: 'block', width: '100%', textAlign: 'left',
                                                    padding: '10px 16px', fontSize: '13px', border: 'none',
                                                    background: t.id === activeTenantId ? '#e8f0fe' : '#fff',
                                                    fontWeight: t.id === activeTenantId ? 700 : 400,
                                                    cursor: 'pointer', color: '#1a1a2e',
                                                }}
                                                onMouseEnter={e => { e.currentTarget.style.background = '#e8f0fe'; }}
                                                onMouseLeave={e => { e.currentTarget.style.background = t.id === activeTenantId ? '#e8f0fe' : '#fff'; }}
                                            >
                                                {t.display_name || t.id}
                                                {t.id === activeTenantId && <span style={{ marginLeft: '6px', color: '#007bff' }}>✓</span>}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <button onClick={() => { logout(); navigate('/login'); }}
                            style={{
                                padding: '5px 14px', fontSize: '12px', fontWeight: 600,
                                backgroundColor: 'rgba(220,53,69,.15)',
                                border: '1px solid rgba(220,53,69,.4)',
                                borderRadius: '6px', cursor: 'pointer', color: '#ff8a8a',
                                transition: 'all .15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(220,53,69,.35)'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(220,53,69,.15)'; }}
                        >
                            Logga ut
                        </button>
                        <button className="nav-hamburger" onClick={() => setMenuOpen(v => !v)} aria-label="Visa eller dölj menyn">
                            {menuOpen ? '✕' : '☰'}
                        </button>
                    </div>
                </div>
                <nav className={`nav-menu${menuOpen ? ' open' : ''}`} style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    {NAV_GROUPS
                        .map(g => ({ ...g, items: g.items.filter(visible) }))
                        .filter(g => g.items.length > 0)
                        .map(g => (
                            <div key={g.title ?? 'top'} className="nav-group">
                                {g.title && <div className="nav-group-label">{g.title}</div>}
                                <div className="nav-group-items">
                                    {g.items.map(item => {
                                        const active = isActive(item.path);
                                        return (
                                            <button key={item.path} onClick={() => { navigate(item.path); setMenuOpen(false); }} style={{
                                                padding: '7px 14px', fontSize: '13px', fontWeight: 600,
                                                backgroundColor: active ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.07)',
                                                color: active ? '#fff' : '#c0c8d4',
                                                border: active ? '1px solid rgba(255,255,255,.3)' : '1px solid rgba(255,255,255,.1)',
                                                borderRadius: '8px',
                                                cursor: 'pointer', transition: 'all .15s',
                                            }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,.22)';
                                                    e.currentTarget.style.color = '#fff';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.backgroundColor = active ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.07)';
                                                    e.currentTarget.style.color = active ? '#fff' : '#c0c8d4';
                                                }}
                                            >
                                                {item.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    {push.available && (
                        <div className="nav-group nav-group-mobile-only">
                            <div className="nav-group-label">Den här enheten</div>
                            <div className="nav-group-items">
                                <button disabled={push.busy} onClick={push.on ? push.disable : push.enable} style={{
                                    padding: '7px 14px', fontSize: '13px', fontWeight: 600,
                                    backgroundColor: 'rgba(255,255,255,.07)', color: '#c0c8d4',
                                    border: '1px solid rgba(255,255,255,.1)', borderRadius: '8px', cursor: 'pointer',
                                }}>
                                    {push.busy ? '…' : push.on ? '🔕 Stäng av notiser' : '🔔 Slå på notiser'}
                                </button>
                            </div>
                        </div>
                    )}
                </nav>
            </div>

            {/* ── Page Content ────────────────────────────────────── */}
            <div className="layout-content" style={{ padding: '24px 28px', maxWidth: '1400px', margin: '0 auto' }}>
                {children}
            </div>

            {/* ── Bottom Navigation (mobile only) ─────────────────── */}
            <nav className="bottom-nav" aria-label="Meny längst ned">
                {BOTTOM_NAV_ITEMS.filter(visible).map(item => {
                    const active = isActive(item.path);
                    return (
                        <button
                            key={item.path}
                            className={`bottom-nav-item${active ? ' active' : ''}`}
                            onClick={() => { navigate(item.path); setMenuOpen(false); }}
                            aria-label={item.label}
                            aria-current={active ? 'page' : undefined}
                        >
                            <span className="bottom-nav-item-icon">{item.icon}</span>
                            <span>{item.label}</span>
                        </button>
                    );
                })}
                <button
                    className={`bottom-nav-item${menuOpen ? ' active' : ''}`}
                    onClick={() => setMenuOpen(v => !v)}
                    aria-label="Fler sidor"
                    aria-expanded={menuOpen}
                >
                    <span className="bottom-nav-item-icon">{menuOpen ? '✕' : '☰'}</span>
                    <span>Mer</span>
                </button>
            </nav>
        </div>
    );
};

export default Layout;
